/**
 * LiveKit subscriber for commercial COP live A/V.
 * PiP / inline + enlarge dialog, zoom/fit, local record-to-file.
 *
 * When the token carries an e2e shared key, frames are decrypted in the browser
 * with the same key the phone encrypted with; the SFU only relays ciphertext.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PinDropIcon from '@mui/icons-material/PinDrop';
import {
  ExternalE2EEKeyProvider,
  Room,
  RoomEvent,
  Track,
  RemoteTrack,
  isE2EESupported,
} from 'livekit-client';

export type LiveStreamPlayerVariant = 'inline' | 'pip' | 'tile';

interface LiveStreamPlayerProps {
  deviceId: string;
  onClose?: () => void;
  variant?: LiveStreamPlayerVariant;
  label?: string;
  /** When false, disconnect and do not subscribe (used by Streams tab). Default true. */
  enabled?: boolean;
  /** Fly / highlight this device on the COP map. */
  onLocate?: () => void;
  /** Open the map-tied LIVE pip for this device. */
  onWatchOnMap?: () => void;
  /** Mute local playback (other tiles stay subscribed). */
  muted?: boolean;
}

interface TokenResponse {
  url: string;
  token: string;
  room: string;
  role: string;
  device_id: string;
  e2e?: {
    e2e_enabled: boolean;
    e2e_mode: string;
    epoch: number;
    shared_key?: string | null;
  };
}

const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.5;

async function fetchSubscriberToken(deviceId: string): Promise<TokenResponse> {
  const apiUrl = process.env.REACT_APP_API_URL || '/api';
  const accessToken = localStorage.getItem('access_token');
  const res = await fetch(`${apiUrl}/live-stream/token/subscriber`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify({ device_id: deviceId, role: 'subscriber' }),
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(detail || `Token request failed (${res.status})`);
  }
  return res.json();
}

function pickRecorderMime(): string | undefined {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];
  if (typeof MediaRecorder === 'undefined') return undefined;
  return candidates.find((c) => MediaRecorder.isTypeSupported(c));
}

const LiveStreamPlayer: React.FC<LiveStreamPlayerProps> = ({
  deviceId,
  onClose,
  variant = 'inline',
  label,
  enabled = true,
  onLocate,
  onWatchOnMap,
  muted = false,
}) => {
  const pipVideoRef = useRef<HTMLVideoElement | null>(null);
  const expandedVideoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const roomRef = useRef<Room | null>(null);
  const videoTrackRef = useRef<RemoteTrack | null>(null);
  const audioTrackRef = useRef<RemoteTrack | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordChunksRef = useRef<Blob[]>([]);
  const recordStartedAtRef = useRef<number>(0);
  const expandedRef = useRef(false);

  const [status, setStatus] = useState<'connecting' | 'live' | 'error' | 'idle'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [hasVideo, setHasVideo] = useState(false);
  const [aspect, setAspect] = useState(9 / 16);
  const [zoom, setZoom] = useState(1);
  /** contain = full frame visible (fit); cover = fill frame (crop). */
  const [objectFit, setObjectFit] = useState<'contain' | 'cover'>('contain');
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const isPip = variant === 'pip';
  const isTile = variant === 'tile';

  useEffect(() => {
    expandedRef.current = expanded;
  }, [expanded]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
    if (pipVideoRef.current) pipVideoRef.current.muted = muted;
    if (expandedVideoRef.current) expandedVideoRef.current.muted = muted;
  }, [muted]);

  const bindVideoToActiveElement = useCallback((preferExpanded: boolean) => {
    const track = videoTrackRef.current;
    if (!track) return;
    const target = preferExpanded ? expandedVideoRef.current : pipVideoRef.current;
    if (!target) return;
    track.detach();
    track.attach(target);
    void target.play().catch(() => undefined);
  }, []);

  const onVideoMeta = (el: HTMLVideoElement | null) => {
    if (!el || !el.videoWidth || !el.videoHeight) return;
    setAspect(el.videoWidth / el.videoHeight);
  };

  useEffect(() => {
    if (!enabled) {
      setStatus('idle');
      setHasVideo(false);
      setError(null);
      setExpanded(false);
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch {
        /* ignore */
      }
      try {
        videoTrackRef.current?.detach();
        audioTrackRef.current?.detach();
        roomRef.current?.disconnect();
      } catch {
        /* ignore */
      }
      roomRef.current = null;
      videoTrackRef.current = null;
      audioTrackRef.current = null;
      return;
    }

    let cancelled = false;
    let reconnectTimer: number | null = null;
    let attempt = 0;
    let e2eeWorker: Worker | null = null;

    const disposeE2eeWorker = () => {
      e2eeWorker?.terminate();
      e2eeWorker = null;
    };

    const attachTrack = (track: RemoteTrack) => {
      if (track.kind === Track.Kind.Video) {
        videoTrackRef.current = track;
        setHasVideo(true);
        bindVideoToActiveElement(expandedRef.current);
        setStatus('live');
      } else if (track.kind === Track.Kind.Audio) {
        audioTrackRef.current = track;
        if (audioRef.current) {
          track.attach(audioRef.current);
        }
        setStatus((s) => (s === 'error' ? s : 'live'));
      }
    };

    /** LIVE must mean "media is arriving", not "the socket opened". */
    const demoteIfNoMedia = () => {
      if (videoTrackRef.current || audioTrackRef.current) return;
      setStatus((s) => (s === 'error' || s === 'idle' ? s : 'connecting'));
    };

    const attachExisting = (room: Room) => {
      room.remoteParticipants.forEach((p) => {
        p.trackPublications.forEach((pub) => {
          if (pub.isSubscribed && pub.track) {
            attachTrack(pub.track as RemoteTrack);
          }
        });
      });
    };

    const wireRoom = (room: Room) => {
      room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => attachTrack(track));
      room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        if (track.kind === Track.Kind.Video) {
          if (videoTrackRef.current === track) {
            videoTrackRef.current = null;
            setHasVideo(false);
          }
        } else if (track.kind === Track.Kind.Audio) {
          // Was never cleared, so a departed publisher left the tile stuck on
          // the "Audio only" placeholder under a red LIVE badge.
          if (audioTrackRef.current === track) {
            audioTrackRef.current = null;
          }
        }
        demoteIfNoMedia();
      });
      room.on(RoomEvent.ParticipantConnected, () => attachExisting(room));
      room.on(RoomEvent.ParticipantDisconnected, () => {
        // Still joined to the room, but there is nothing left to show.
        if (!room.remoteParticipants.size) {
          videoTrackRef.current = null;
          audioTrackRef.current = null;
          setHasVideo(false);
          demoteIfNoMedia();
        }
      });
      room.on(RoomEvent.Disconnected, () => {
        if (cancelled) return;
        setStatus('connecting');
        scheduleReconnect();
      });
    };

    const connectOnce = async () => {
      if (cancelled) return;
      setStatus('connecting');
      setError(null);
      try {
        const tok = await fetchSubscriberToken(deviceId);
        if (cancelled) return;
        const sharedKey = tok.e2e?.e2e_enabled ? tok.e2e.shared_key : null;
        if (sharedKey && !isE2EESupported()) {
          throw new Error('This browser cannot decrypt the encrypted stream. Use a current Chrome, Edge or Safari.');
        }
        // Fresh Room per attempt — LiveKit rooms are not cleanly reusable after disconnect.
        const prev = roomRef.current;
        if (prev) {
          try {
            prev.disconnect();
          } catch {
            /* ignore */
          }
        }
        disposeE2eeWorker();
        let keyProvider: ExternalE2EEKeyProvider | null = null;
        if (sharedKey) {
          keyProvider = new ExternalE2EEKeyProvider();
          e2eeWorker = new Worker(new URL('livekit-client/e2ee-worker', import.meta.url));
        }
        const room = new Room({
          adaptiveStream: true,
          dynacast: true,
          ...(keyProvider && e2eeWorker ? { e2ee: { keyProvider, worker: e2eeWorker } } : {}),
        });
        roomRef.current = room;
        wireRoom(room);
        if (keyProvider && sharedKey) {
          // Each fresh token carries the current epoch's key, so a reconnect
          // also picks up a key the phone rotated for a new broadcast.
          await keyProvider.setKey(sharedKey);
          await room.setE2EEEnabled(true);
        }
        await room.connect(tok.url, tok.token);
        if (cancelled) {
          room.disconnect();
          return;
        }
        attempt = 0;
        // attachTrack promotes to 'live' when real media lands. Joining an empty
        // room used to promote anyway, which is how a stalled or absent
        // publisher still showed a LIVE badge over a black tile.
        attachExisting(room);
        demoteIfNoMedia();
      } catch (e: any) {
        if (!cancelled) {
          setStatus('error');
          setError(e?.message || 'Failed to join live stream');
          scheduleReconnect();
        }
      }
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      if (reconnectTimer != null) window.clearTimeout(reconnectTimer);
      attempt += 1;
      const delay = Math.min(10_000, 1000 * attempt);
      reconnectTimer = window.setTimeout(() => {
        void connectOnce();
      }, delay);
    };

    void connectOnce();

    return () => {
      cancelled = true;
      if (reconnectTimer != null) window.clearTimeout(reconnectTimer);
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch {
        /* ignore */
      }
      try {
        videoTrackRef.current?.detach();
        audioTrackRef.current?.detach();
      } catch {
        /* ignore */
      }
      const room = roomRef.current;
      const worker = e2eeWorker;
      e2eeWorker = null;
      try {
        if (room) {
          void room.disconnect().finally(() => worker?.terminate());
        } else {
          worker?.terminate();
        }
      } catch {
        worker?.terminate();
      }
      roomRef.current = null;
      videoTrackRef.current = null;
      audioTrackRef.current = null;
    };
  }, [deviceId, enabled, bindVideoToActiveElement]);

  useEffect(() => {
    if (!hasVideo) return;
    const id = window.requestAnimationFrame(() => bindVideoToActiveElement(expanded));
    return () => window.cancelAnimationFrame(id);
  }, [expanded, hasVideo, bindVideoToActiveElement]);

  useEffect(() => {
    if (!recording) return;
    const id = window.setInterval(() => {
      const started = recordStartedAtRef.current;
      if (!started) return;
      setRecordSeconds(Math.floor((Date.now() - started) / 1000));
    }, 500);
    return () => window.clearInterval(id);
  }, [recording]);

  const setExpandedVideoNode = (el: HTMLVideoElement | null) => {
    expandedVideoRef.current = el;
    if (el && expanded && videoTrackRef.current) {
      bindVideoToActiveElement(true);
      onVideoMeta(el);
    }
  };

  const zoomIn = () => setZoom((z) => Math.min(ZOOM_MAX, Math.round((z + ZOOM_STEP) * 10) / 10));
  const zoomOut = () => setZoom((z) => Math.max(ZOOM_MIN, Math.round((z - ZOOM_STEP) * 10) / 10));

  const zoomFit = () => {
    setZoom(1);
    setObjectFit('contain');
  };

  const downloadRecording = (blob: Blob) => {
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    const safeLabel = (label || deviceId || 'device').replace(/[^\w.-]+/g, '_');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pathfinder-live-${safeLabel}-${stamp}.webm`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
  };

  const stopRecording = () => {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state === 'inactive') {
      setRecording(false);
      return;
    }
    try {
      rec.stop();
    } catch {
      setRecording(false);
    }
  };

  const startRecording = () => {
    if (recording) return;
    const videoMst = videoTrackRef.current?.mediaStreamTrack;
    const audioMst = audioTrackRef.current?.mediaStreamTrack;
    if (!videoMst && !audioMst) {
      setError('Nothing to record yet');
      return;
    }
    if (typeof MediaRecorder === 'undefined') {
      setError('Recording is not supported in this browser');
      return;
    }
    const tracks: MediaStreamTrack[] = [];
    if (videoMst) tracks.push(videoMst);
    if (audioMst) tracks.push(audioMst);
    const stream = new MediaStream(tracks);
    const mime = pickRecorderMime();
    let recorder: MediaRecorder;
    try {
      recorder = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream);
    } catch (e: any) {
      setError(e?.message || 'Could not start recorder');
      return;
    }
    recordChunksRef.current = [];
    recordStartedAtRef.current = Date.now();
    setRecordSeconds(0);
    recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) recordChunksRef.current.push(ev.data);
    };
    recorder.onerror = () => {
      setRecording(false);
      setError('Recording failed');
    };
    recorder.onstop = () => {
      setRecording(false);
      mediaRecorderRef.current = null;
      const chunks = recordChunksRef.current;
      recordChunksRef.current = [];
      if (!chunks.length) return;
      const blob = new Blob(chunks, { type: mime || 'video/webm' });
      downloadRecording(blob);
    };
    mediaRecorderRef.current = recorder;
    recorder.start(1000);
    setRecording(true);
  };

  const toggleRecording = () => {
    if (recording) stopRecording();
    else startRecording();
  };

  const formatRecTime = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const headerBar = (compact: boolean) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: compact ? 0.75 : 1.5,
        py: compact ? 0.25 : 0.75,
        bgcolor: '#dc2626',
        gap: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: compact ? 10 : 13,
            fontWeight: 800,
            color: '#fff',
            letterSpacing: 0.6,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          LIVE{label || deviceId ? ` · ${label || deviceId}` : ''}
        </Typography>
        {recording && (
          <Typography
            sx={{
              fontSize: compact ? 9 : 11,
              fontWeight: 700,
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.35)',
              px: 0.6,
              borderRadius: 0.5,
              whiteSpace: 'nowrap',
            }}
          >
            ● REC {formatRecTime(recordSeconds)}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1, flexShrink: 0 }}>
        {onLocate && (
          <IconButton
            size="small"
            onClick={onLocate}
            sx={{ p: compact ? 0.15 : 0.5, color: '#fff' }}
            aria-label="Locate on map"
            title="Locate on map"
          >
            <MyLocationIcon sx={{ fontSize: compact ? 14 : 20 }} />
          </IconButton>
        )}
        {onWatchOnMap && (
          <IconButton
            size="small"
            onClick={onWatchOnMap}
            sx={{ p: compact ? 0.15 : 0.5, color: '#fff' }}
            aria-label="Watch on map pin"
            title="Watch on map pin"
          >
            <PinDropIcon sx={{ fontSize: compact ? 14 : 20 }} />
          </IconButton>
        )}
        <IconButton
          size="small"
          onClick={toggleRecording}
          disabled={status !== 'live'}
          sx={{
            p: compact ? 0.15 : 0.5,
            color: recording ? '#fecaca' : '#fff',
            opacity: status !== 'live' ? 0.4 : 1,
          }}
          aria-label={recording ? 'Stop recording' : 'Record live feed'}
          title={recording ? 'Stop & download recording' : 'Record feed (downloads WebM)'}
        >
          {recording ? (
            <StopCircleIcon sx={{ fontSize: compact ? 14 : 20 }} />
          ) : (
            <FiberManualRecordIcon sx={{ fontSize: compact ? 14 : 20 }} />
          )}
        </IconButton>
        {!isTile && (
          <>
            <IconButton
              size="small"
              onClick={zoomOut}
              disabled={zoom <= ZOOM_MIN}
              sx={{ p: compact ? 0.15 : 0.5, color: '#fff', opacity: zoom <= ZOOM_MIN ? 0.4 : 1 }}
              aria-label="Zoom out"
              title="Zoom out"
            >
              <ZoomOutIcon sx={{ fontSize: compact ? 14 : 20 }} />
            </IconButton>
            <Typography sx={{ fontSize: compact ? 9 : 11, color: '#fff', minWidth: compact ? 22 : 28, textAlign: 'center' }}>
              {zoom.toFixed(1)}x
            </Typography>
            <IconButton
              size="small"
              onClick={zoomIn}
              disabled={zoom >= ZOOM_MAX}
              sx={{ p: compact ? 0.15 : 0.5, color: '#fff', opacity: zoom >= ZOOM_MAX ? 0.4 : 1 }}
              aria-label="Zoom in"
              title="Zoom in"
            >
              <ZoomInIcon sx={{ fontSize: compact ? 14 : 20 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={zoomFit}
              sx={{ p: compact ? 0.15 : 0.5, color: '#fff', opacity: zoom === 1 && objectFit === 'contain' ? 0.5 : 1 }}
              aria-label="Fit video to frame"
              title="Fit (reset zoom, show full frame)"
            >
              <FitScreenIcon sx={{ fontSize: compact ? 14 : 20 }} />
            </IconButton>
          </>
        )}
        <IconButton
          size="small"
          onClick={() => {
            setExpanded((v) => !v);
            if (!expanded) {
              setZoom(1);
              setObjectFit('contain');
            }
          }}
          sx={{ p: compact ? 0.15 : 0.5, color: '#fff' }}
          aria-label={expanded ? 'Shrink live viewer' : 'Enlarge live viewer'}
          title={expanded ? 'Shrink' : 'Expand'}
        >
          {expanded ? (
            <CloseFullscreenIcon sx={{ fontSize: compact ? 14 : 20 }} />
          ) : (
            <OpenInFullIcon sx={{ fontSize: compact ? 14 : 20 }} />
          )}
        </IconButton>
        {onClose && (
          <IconButton
            size="small"
            onClick={() => {
              if (recording) stopRecording();
              onClose();
            }}
            sx={{ p: compact ? 0.15 : 0.5, color: '#fff' }}
            aria-label="Close live viewer"
          >
            <CloseIcon sx={{ fontSize: compact ? 14 : 20 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );

  const statusOverlay = (compact: boolean) => (
    <>
      {status === 'connecting' && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            bgcolor: 'rgba(0,0,0,0.55)',
            zIndex: 1,
          }}
        >
          <CircularProgress size={compact ? 14 : 22} sx={{ color: '#fca5a5' }} />
          <Typography sx={{ fontSize: compact ? 10 : 13, color: '#e5e7eb' }}>Connecting</Typography>
        </Box>
      )}
      {status === 'error' && (
        <Box sx={{ p: compact ? 0.75 : 2, position: 'absolute', inset: 0, zIndex: 1 }}>
          <Typography sx={{ fontSize: compact ? 10 : 13, color: '#fca5a5' }}>{error}</Typography>
        </Box>
      )}
      {status === 'live' && !hasVideo && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#111',
            zIndex: 1,
          }}
        >
          <Typography sx={{ fontSize: compact ? 10 : 14, color: '#e5e7eb', fontWeight: 600 }}>
            Audio only
          </Typography>
        </Box>
      )}
    </>
  );

  const videoStyle = (fill: 'cover' | 'contain'): React.CSSProperties => ({
    width: '100%',
    height: '100%',
    objectFit: fill,
    background: '#000',
    display: status === 'error' || !hasVideo ? 'none' : 'block',
    transform: `scale(${zoom})`,
    transformOrigin: 'center center',
    transition: 'transform 0.15s ease',
  });

  const onWheelZoom = (e: React.WheelEvent) => {
    e.stopPropagation();
    if (e.deltaY < 0) zoomIn();
    else zoomOut();
  };

  const pipOrInline = isPip || isTile ? (
    <Box
      sx={{
        width: isTile ? '100%' : expanded ? 0 : 168,
        height: isTile ? '100%' : expanded ? 0 : 'auto',
        visibility: !isTile && expanded ? 'hidden' : 'visible',
        position: !isTile && expanded ? 'absolute' : 'relative',
        pointerEvents: !isTile && expanded ? 'none' : 'auto',
        bgcolor: 'rgba(17, 17, 17, 0.92)',
        border: !isTile && expanded ? 'none' : '2px solid #dc2626',
        borderRadius: 1,
        overflow: 'hidden',
        boxShadow: isTile || expanded ? 'none' : '0 4px 14px rgba(0,0,0,0.45)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: isTile ? 0 : undefined,
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => {
        e.stopPropagation();
        setZoom(1);
        setObjectFit('contain');
        setExpanded(true);
      }}
      onWheel={isTile ? undefined : onWheelZoom}
    >
      {headerBar(true)}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: isTile ? undefined : 112,
          flex: isTile ? 1 : undefined,
          minHeight: isTile ? 100 : undefined,
          bgcolor: '#111',
          overflow: 'hidden',
        }}
      >
        {statusOverlay(true)}
        {status === 'idle' && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#111',
              zIndex: 1,
            }}
          >
            <Typography sx={{ fontSize: 10, color: '#9ca3af' }}>Standby</Typography>
          </Box>
        )}
        <video
          ref={pipVideoRef}
          autoPlay
          playsInline
          muted={false}
          onLoadedMetadata={(e) => onVideoMeta(e.currentTarget)}
          // Portrait sources (screen share / portrait camera) fit the whole frame to
          // the player; landscape camera still fills to avoid letterboxing.
          style={videoStyle(aspect < 1 ? 'contain' : 'cover')}
        />
      </Box>
      {(label || deviceId) && !isTile && (
        <Typography
          sx={{
            px: 0.75,
            py: 0.35,
            fontSize: 10,
            color: '#e5e7eb',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label || deviceId}
        </Typography>
      )}
      <audio ref={audioRef} autoPlay />
    </Box>
  ) : (
    <Box sx={{ mt: 1, minWidth: 260, display: expanded ? 'none' : 'block' }}>
      {headerBar(false)}
      <Box
        sx={{ position: 'relative', width: '100%', height: 200, bgcolor: '#111', overflow: 'hidden' }}
        onWheel={onWheelZoom}
      >
        {statusOverlay(false)}
        <video
          ref={pipVideoRef}
          autoPlay
          playsInline
          muted={false}
          onLoadedMetadata={(e) => onVideoMeta(e.currentTarget)}
          style={videoStyle(aspect < 1 ? 'contain' : 'cover')}
        />
        {status === 'live' && !hasVideo && <Box sx={{ height: 120 }} />}
      </Box>
      <audio ref={audioRef} autoPlay />
    </Box>
  );

  return (
    <>
      {pipOrInline}
      <Dialog
        open={expanded}
        onClose={() => setExpanded(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0a0a0a',
            backgroundImage: 'none',
            border: '2px solid #dc2626',
            width: 'min(96vw, 1400px)',
            height: 'min(92vh, 900px)',
            maxWidth: '96vw',
            maxHeight: '92vh',
            m: 1,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {headerBar(false)}
        <DialogContent
          sx={{
            p: 0,
            bgcolor: '#000',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              flex: 1,
              width: '100%',
              height: '100%',
              bgcolor: '#000',
              overflow: 'hidden',
              // Keep phone portrait readable but allow landscape to use the full dialog.
              maxWidth: aspect < 1 ? `min(100%, calc((92vh - 48px) * ${aspect}))` : '100%',
              mx: 'auto',
              aspectRatio: aspect < 1 ? `${aspect}` : 'auto',
            }}
            onWheel={onWheelZoom}
            onDoubleClick={zoomFit}
          >
            {statusOverlay(false)}
            <video
              ref={setExpandedVideoNode}
              autoPlay
              playsInline
              muted={false}
              onLoadedMetadata={(e) => onVideoMeta(e.currentTarget)}
              style={videoStyle(objectFit)}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LiveStreamPlayer;
